import { useState, useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';
import { ProjectModal } from './ProjectModal';

/**
 * Subsystem status type matching the API response.
 */
type SubsystemStatus = 'ok' | 'error';

/**
 * Health status for a single subsystem.
 */
interface SubsystemHealth {
  status: SubsystemStatus;
  latencyMs?: number;
  error?: string;
}

/**
 * Complete health check response from the API.
 */
interface HealthResponse {
  status: 'ok' | 'degraded' | 'error';
  timestamp: string;
  subsystems: {
    database: SubsystemHealth;
    redis: SubsystemHealth;
    telegram: SubsystemHealth;
    openrouter: SubsystemHealth;
  };
}

/**
 * Configuration for subsystem display.
 */
interface SubsystemConfig {
  key: keyof HealthResponse['subsystems'];
  label: string;
  icon: string;
}

/**
 * Subsystem display configuration.
 */
const SUBSYSTEM_CONFIG: SubsystemConfig[] = [
  { key: 'database', label: 'Database', icon: '🗄️' },
  { key: 'redis', label: 'Redis', icon: '⚡' },
  { key: 'telegram', label: 'Telegram', icon: '📱' },
  { key: 'openrouter', label: 'AI Provider', icon: '🤖' },
];

const buildCommit = import.meta.env.VITE_ADMIN_BUILD_COMMIT || 'unknown';
const buildDateUtc = import.meta.env.VITE_ADMIN_BUILD_DATE_UTC || 'unknown';

function formatBuildDate(value: string): string {
  if (!value || value === 'unknown') {
    return 'unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Navigation item configuration.
 */
interface NavItem {
  path: string;
  label: string;
  icon: string;
}

/**
 * Navigation section configuration.
 */
interface NavSection {
  title: string;
  items: NavItem[];
  requiresBot?: boolean;
}

/**
 * System-level navigation items (always visible).
 */
const systemNavItems: NavItem[] = [
  { path: '/bots', label: 'All Bots', icon: '◉' },
  { path: '/api-expenses', label: 'API Expenses', icon: '💸' },
  { path: '/tools', label: 'Tools', icon: '🔧' },
  { path: '/settings', label: 'Settings', icon: '⚙' },
];

/**
 * Get project-specific navigation items (require a bot to be selected).
 * Uses context-aware paths that don't include bot IDs in the URL.
 */
function getProjectNavItems(): NavItem[] {
  return [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/bot/details', label: 'Details', icon: '📋' },
    { path: '/bot/edit', label: 'Edit Bot', icon: '✏️' },
    { path: '/bot/test', label: 'Test Bot', icon: '🧪' },
    { path: '/communication-plan', label: 'Communication Plan', icon: '📨' },
    { path: '/users', label: 'Users', icon: '👥' },
    { path: '/safety', label: 'Safety', icon: '🛡️' },
    { path: '/support', label: 'Support', icon: '💬' },
    { path: '/media', label: 'Media Library', icon: '🖼️' },
    { path: '/analytics', label: 'Analytics', icon: '📈' },
    { path: '/onboarding', label: 'Onboarding', icon: '🛤️' },
  ];
}

/**
 * Get navigation sections for the sidebar.
 * @param hasBot - Whether a bot is currently selected
 */
function getNavSections(hasBot: boolean): NavSection[] {
  return [
    { title: 'Project', items: hasBot ? getProjectNavItems() : [], requiresBot: true },
    { title: 'System', items: systemNavItems, requiresBot: false },
  ];
}

/**
 * Main layout component with sidebar navigation.
 *
 * Provides a consistent navigation structure across all pages.
 * The sidebar shows the current bot branding and allows navigation between routes.
 */
export function Layout() {
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isStatusExpanded, setIsStatusExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const healthQuery = trpc.health.useQuery(undefined, {
    refetchInterval: 30000, // Check health every 30 seconds
  });
  const { currentBot, bots, selectBot, isLoading: botsLoading } = useProject();
  const formattedBuildDate = formatBuildDate(buildDateUtc);
  const shortBuildCommit = buildCommit === 'unknown' ? 'unknown' : buildCommit.slice(0, 12);
  const buildDateDisplay = formattedBuildDate === 'unknown' ? 'unknown' : `${formattedBuildDate} UTC`;

  // Fetch support ticket stats for unread count badge
  const supportStatsQuery = trpc.support.stats.useQuery(
    { botId: currentBot?.id },
    {
      enabled: !!currentBot,
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Cast health data to proper type
  const healthData = healthQuery.data as HealthResponse | undefined;

  // Compute status summary
  const statusSummary = useMemo(() => {
    if (!healthData?.subsystems) {
      return { healthy: 0, total: 4, allOk: false };
    }
    const subsystems = Object.values(healthData.subsystems);
    const healthy = subsystems.filter(s => s.status === 'ok').length;
    return {
      healthy,
      total: subsystems.length,
      allOk: healthy === subsystems.length,
    };
  }, [healthData]);

  // Get overall status color
  const getStatusColor = () => {
    if (healthQuery.isLoading) return '#9B9285'; // Gray while loading
    if (!healthData) return '#C9A090'; // Tan for error/no data
    switch (healthData.status) {
      case 'ok': return '#5A8A5E'; // Green
      case 'degraded': return '#D4A574'; // Orange/amber
      case 'error': return '#C9A090'; // Red/tan
      default: return '#C9A090';
    }
  };

  // Get subsystem status color
  const getSubsystemColor = (status: SubsystemStatus) => {
    return status === 'ok' ? '#5A8A5E' : '#C9A090';
  };

  // Get status text
  const getStatusText = () => {
    if (healthQuery.isLoading) return 'Connecting...';
    if (!healthData) return 'Disconnected';
    switch (healthData.status) {
      case 'ok': return 'System Status';
      case 'degraded': return 'Degraded';
      case 'error': return 'System Error';
      default: return 'Unknown';
    }
  };

  // Get status subtext
  const getStatusSubtext = () => {
    if (healthQuery.isLoading) return '';
    if (!healthData) return 'Check connection';
    if (healthData.status === 'ok') {
      return 'All services running';
    }
    return `${statusSummary.healthy}/${statusSummary.total} services healthy`;
  };

  return (
    <div style={styles.container}>
      {/* Project Selector Modal */}
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        bots={bots}
        currentBotId={currentBot?.id ?? null}
        onSelectBot={selectBot}
      />

      {/* Sidebar */}
      <aside style={{
        ...styles.sidebar,
        width: isCollapsed ? '68px' : '260px',
      }}>
        {/* Logo Header */}
        <div style={{
          ...styles.sidebarHeader,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          padding: isCollapsed ? '20px 12px' : '20px 24px',
        }}>
          <span style={styles.logoIcon}>✦</span>
          {!isCollapsed && <span style={styles.logoText}>WhatsApp Admin</span>}
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            ...styles.collapseButton,
            left: isCollapsed ? '52px' : '244px',
          }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span style={{
            ...styles.collapseIcon,
            transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            ‹
          </span>
        </button>

        {/* Project Selector */}
        <div style={{
          ...styles.projectSelector,
          padding: isCollapsed ? '16px 10px' : '16px 20px',
        }}>
          {botsLoading ? (
            <div style={styles.projectSelectorLoading}>{isCollapsed ? '...' : 'Loading...'}</div>
          ) : bots.length === 0 ? (
            <div style={styles.projectSelectorEmpty}>{isCollapsed ? '—' : 'No bots available'}</div>
          ) : currentBot ? (
            <button
              onClick={() => setIsProjectModalOpen(true)}
              style={{
                ...styles.projectSelectorButton,
                background: `linear-gradient(135deg, ${currentBot.color || '#3b82f6'}25 0%, ${currentBot.color || '#3b82f6'}10 100%)`,
                border: `1px solid ${currentBot.color || '#3b82f6'}40`,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                padding: isCollapsed ? '12px 10px' : '12px 14px',
              }}
              title={isCollapsed ? currentBot.name : 'Click to switch project'}
            >
              <span style={styles.projectIcon}>{currentBot.icon || '🤖'}</span>
              {!isCollapsed && (
                <>
                  <div style={styles.projectInfo}>
                    <p style={styles.projectName}>{currentBot.name}</p>
                    <p style={styles.projectSlug}>{currentBot.slug}</p>
                  </div>
                  <span style={styles.projectChevron}>↓</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => setIsProjectModalOpen(true)}
              style={{
                ...styles.projectSelectorButton,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                padding: isCollapsed ? '12px 10px' : '12px 14px',
              }}
              title={isCollapsed ? 'Select project' : 'Click to select project'}
            >
              <span style={styles.projectIcon}>🤖</span>
              {!isCollapsed && (
                <>
                  <div style={styles.projectInfo}>
                    <p style={styles.projectName}>Select Project</p>
                    <p style={styles.projectSlug}>Click to choose</p>
                  </div>
                  <span style={styles.projectChevron}>↓</span>
                </>
              )}
            </button>
          )}
        </div>

        <nav style={{
          ...styles.nav,
          padding: isCollapsed ? '0 8px' : '0 16px',
        }}>
          {getNavSections(!!currentBot).map((section) => {
            // Hide section if it requires a bot and no bot is selected
            if (section.requiresBot && !currentBot) {
              return null;
            }

            return (
              <div key={section.title} style={styles.navSection}>
                {!isCollapsed && (
                  <p style={styles.navSectionHeader}>
                    {section.title}
                  </p>
                )}
                {isCollapsed && section.title === 'System' && (
                  <div style={styles.navSectionDivider} />
                )}
                {section.items.map((item) => {
                  const unreadCount = item.path === '/support' ? (supportStatsQuery.data?.unreadCount ?? 0) : 0;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      style={({ isActive }) => ({
                        ...styles.navLink,
                        ...(isActive ? styles.navLinkActive : {}),
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        padding: isCollapsed ? '12px 10px' : '12px 14px',
                      })}
                      title={isCollapsed ? item.label : undefined}
                    >
                      {({ isActive }) => (
                        <>
                          <span style={{
                            ...styles.navIcon,
                            ...(isActive ? styles.navIconActive : {}),
                          }}>{item.icon}</span>
                          {!isCollapsed && <span>{item.label}</span>}
                          {unreadCount > 0 && (
                            <span style={styles.unreadBadge}>
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div style={{
          ...styles.footer,
          padding: isCollapsed ? '16px 10px' : '16px 20px',
        }}>
          {/* Main Status Indicator */}
          <button
            onClick={() => !isCollapsed && setIsStatusExpanded(!isStatusExpanded)}
            style={{
              ...styles.status,
              ...(healthData?.status !== 'ok' && !healthQuery.isLoading ? styles.statusDisconnected : {}),
              ...(healthData?.status === 'degraded' ? styles.statusDegraded : {}),
              cursor: isCollapsed ? 'default' : 'pointer',
              border: 'none',
              width: '100%',
              textAlign: 'left',
              fontFamily: 'inherit',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              padding: isCollapsed ? '12px 10px' : '12px',
            }}
            title={isCollapsed ? getStatusText() : 'Click to view detailed system status'}
          >
            <span
              style={{
                ...styles.statusDot,
                backgroundColor: getStatusColor(),
              }}
            />
            {!isCollapsed && (
              <>
                <div style={{ flex: 1 }}>
                  <p style={styles.statusText}>{getStatusText()}</p>
                  {!healthQuery.isLoading && (
                    <p style={styles.statusSubtext}>{getStatusSubtext()}</p>
                  )}
                </div>
                <span style={{
                  ...styles.statusChevron,
                  transform: isStatusExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                  ▼
                </span>
              </>
            )}
          </button>

          {/* Expanded Status Panel */}
          {!isCollapsed && isStatusExpanded && !healthQuery.isLoading && healthData?.subsystems && (
            <div style={styles.statusPanel}>
              {SUBSYSTEM_CONFIG.map(({ key, label, icon }) => {
                const subsystem = healthData.subsystems[key];
                return (
                  <div
                    key={key}
                    style={{
                      ...styles.subsystemRow,
                      ...(subsystem.status === 'error' ? styles.subsystemError : {}),
                    }}
                    title={subsystem.error || (subsystem.latencyMs ? `${subsystem.latencyMs}ms` : '')}
                  >
                    <span style={styles.subsystemIcon}>{icon}</span>
                    <span style={styles.subsystemLabel}>{label}</span>
                    <span
                      style={{
                        ...styles.subsystemDot,
                        backgroundColor: getSubsystemColor(subsystem.status),
                      }}
                    />
                    {subsystem.latencyMs !== undefined && subsystem.status === 'ok' && (
                      <span style={styles.subsystemLatency}>{subsystem.latencyMs}ms</span>
                    )}
                    {subsystem.status === 'error' && (
                      <span style={styles.subsystemErrorText}>Error</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!isCollapsed && (
            <div style={styles.buildMetadata}>
              <p style={styles.buildMetadataTitle}>Admin Build</p>
              <div style={styles.buildMetadataRow}>
                <span style={styles.buildMetadataLabel}>Commit</span>
                <code style={styles.buildMetadataValue} title={buildCommit}>
                  {shortBuildCommit}
                </code>
              </div>
              <div style={styles.buildMetadataRow}>
                <span style={styles.buildMetadataLabel}>Built</span>
                <span style={styles.buildMetadataValue} title={buildDateUtc}>
                  {buildDateDisplay}
                </span>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      {/* Key based on bot ID forces React to remount the entire page tree when bot changes,
          ensuring all local component state (pagination, filters, scroll position) resets cleanly */}
      <main key={currentBot?.id ?? 'no-bot'} style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #FDFCFB 0%, #F8F6F3 100%)',
  },
  sidebar: {
    width: '260px',
    background: 'linear-gradient(180deg, #2C2A27 0%, #1E1D1B 100%)',
    color: '#FAF9F7',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    position: 'sticky',
    top: 0,
    transition: 'width 0.2s ease',
  },
  collapseButton: {
    position: 'absolute',
    top: '72px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: '#2C2A27',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#9B9285',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    transition: 'left 0.2s ease, background 0.15s ease, color 0.15s ease',
  },
  collapseIcon: {
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: 1,
    transition: 'transform 0.2s ease',
  },
  sidebarHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoIcon: {
    fontSize: '18px',
    color: '#C9BBA8',
  },
  logoText: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '20px',
    fontWeight: 500,
    color: '#FAF9F7',
  },
  projectSelector: {
    padding: '16px 20px',
  },
  projectSelectorLoading: {
    padding: '12px 14px',
    fontSize: '13px',
    color: '#9B9285',
  },
  projectSelectorEmpty: {
    padding: '12px 14px',
    fontSize: '13px',
    color: '#9B9285',
  },
  projectSelectorButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
  },
  projectIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  projectName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#FAF9F7',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  projectSlug: {
    fontSize: '11px',
    color: '#9B9285',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  projectChevron: {
    fontSize: '12px',
    color: '#706A62',
    flexShrink: 0,
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '0 16px',
    flex: 1,
  },
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  navSectionHeader: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#706A62',
    padding: '16px 14px 8px',
    margin: 0,
  },
  navSectionDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.08)',
    margin: '12px 8px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '8px',
    color: '#9B9285',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.15s ease',
    background: 'transparent',
  },
  navLinkActive: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    color: '#FAF9F7',
  },
  navIcon: {
    fontSize: '16px',
    opacity: 0.7,
  },
  navIconActive: {
    opacity: 1,
  },
  unreadBadge: {
    marginLeft: 'auto',
    backgroundColor: '#C9605A',
    color: '#FFFFFF',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '18px',
    textAlign: 'center' as const,
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    marginTop: 'auto',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '10px',
    background: 'rgba(90, 138, 94, 0.1)',
  },
  statusDisconnected: {
    background: 'rgba(201, 160, 144, 0.1)',
  },
  statusDegraded: {
    background: 'rgba(212, 165, 116, 0.1)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusText: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#FAF9F7',
    margin: 0,
  },
  statusSubtext: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  statusChevron: {
    fontSize: '8px',
    color: '#706A62',
    transition: 'transform 0.2s ease',
    flexShrink: 0,
  },
  statusPanel: {
    marginTop: '8px',
    padding: '8px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  buildMetadata: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  buildMetadataTitle: {
    margin: 0,
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#706A62',
  },
  buildMetadataRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '8px',
  },
  buildMetadataLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  buildMetadataValue: {
    margin: 0,
    fontSize: '11px',
    color: '#FAF9F7',
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    textAlign: 'right' as const,
    wordBreak: 'break-word' as const,
  },
  subsystemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.03)',
    transition: 'background 0.15s ease',
  },
  subsystemError: {
    background: 'rgba(201, 160, 144, 0.15)',
  },
  subsystemIcon: {
    fontSize: '12px',
    flexShrink: 0,
  },
  subsystemLabel: {
    fontSize: '11px',
    color: '#9B9285',
    flex: 1,
  },
  subsystemDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  subsystemLatency: {
    fontSize: '10px',
    color: '#706A62',
    minWidth: '32px',
    textAlign: 'right' as const,
  },
  subsystemErrorText: {
    fontSize: '10px',
    color: '#C9A090',
    fontWeight: 500,
  },
  main: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
  },
};

export default Layout;
